/**
 * Attribution Decoration Utility
 * 
 * Adds traffic source attribution to high-value analytics events
 * Only decorates conversion-focused events to avoid noise
 */

import { debugLog } from '../debugUtils';
import { detectTrafficSource } from '../trafficSourceDetection';

/**
 * Remove empty/null/undefined values from an object
 */
const cleanObject = (obj) => {
  const cleaned = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

/**
 * Convert arrays to strings for analytics platform compatibility
 */
const convertArraysToStrings = (obj) => {
  const converted = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Convert arrays to comma-separated strings
      converted[key] = value.join(', ');
    } else {
      converted[key] = value;
    }
  });
  return converted;
};

/**
 * High-value events that should receive attribution decoration
 */
const HIGH_VALUE_EVENTS = [
  'Purchase',
  'Lead', 
  'InitiateCheckout',
  'AddToCart', // Future event
];

/**
 * Check if an event should receive attribution decoration
 */
export const shouldDecorateWithAttribution = (eventName) => {
  return HIGH_VALUE_EVENTS.includes(eventName);
};

/**
 * Decorate event data with meaningful traffic source attribution
 * Only adds attribution for non-generic traffic sources
 */
export const decorateWithAttribution = (eventData = {}) => {
  try {
    const trafficData = detectTrafficSource();
    
    // Only add meaningful attribution (filter out generic "Direct" traffic)
    const meaningfulAttribution = trafficData.tags.filter(
      tag => !['Direct', 'Organic Traffic'].includes(tag)
    );
    
    if (meaningfulAttribution.length === 0) {
      debugLog('🎯 No meaningful attribution to add (generic traffic)');
      return eventData; // No decoration for generic traffic
    }
    
    // Build attribution data object
    const attributionData = {
      // Primary attribution
      traffic_source: trafficData.trafficSource,
      attribution_channel: trafficData.channelTag,
      attribution_type: trafficData.attributionTag,
      
      // Detailed attribution (only for high-value events)
      attribution_tags: meaningfulAttribution,
      utm_source: trafficData.utmParams.utm_source,
      utm_campaign: trafficData.utmParams.utm_campaign,
      utm_medium: trafficData.utmParams.utm_medium,
      utm_content: trafficData.utmParams.utm_content,
      utm_term: trafficData.utmParams.utm_term,
      
      // Session context
      attribution_timestamp: trafficData.timestamp,
      attribution_referrer: trafficData.referrer,
    };

    // Clean up empty values and convert arrays to strings for platform compatibility
    const cleanedAttributionData = cleanObject(attributionData);
    const platformCompatibleData = convertArraysToStrings(cleanedAttributionData);
    const decoratedData = {
      ...eventData,
      ...platformCompatibleData,
    };

    debugLog('🎯 Added attribution decoration:', {
      eventName: eventData.event_name || 'unknown',
      trafficSource: trafficData.trafficSource,
      attributionTags: meaningfulAttribution.length,
      hasUtmParams: !!trafficData.utmParams.utm_source || !!trafficData.utmParams.utm_campaign,
    });

    return decoratedData;
  } catch (error) {
    debugLog('❌ Failed to add attribution decoration:', error);
    return eventData; // Return original data on error
  }
};

/**
 * Get attribution summary for debugging
 */
export const getAttributionSummary = () => {
  try {
    const trafficData = detectTrafficSource();
    const meaningfulAttribution = trafficData.tags.filter(
      tag => !['Direct', 'Organic Traffic'].includes(tag)
    );
    
    return {
      trafficSource: trafficData.trafficSource,
      channelTag: trafficData.channelTag,
      attributionTag: trafficData.attributionTag,
      meaningfulTags: meaningfulAttribution,
      utmParams: trafficData.utmParams,
      timestamp: trafficData.timestamp,
      hasMeaningfulAttribution: meaningfulAttribution.length > 0,
    };
  } catch (error) {
    debugLog('❌ Failed to get attribution summary:', error);
    return null;
  }
}; 